import React, { useState } from 'react';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import { Breadcrumbs } from '../../../components/uikit/Breadcrumbs.tsx';
import Mail from 'assets/icons/mail.svg?react';
import StatusFailed from 'assets/icons/status_failed.svg?react';
import StatusWarning from 'assets/icons/status_warning.svg?react';
import WarningIcon from 'assets/icons/warning-icon.svg?react';
import StatusSuccess from 'assets/icons/success-round.svg?react';
import Search from 'assets/icons/search-2.svg?react';
import styles from './WarrantyInfo.module.scss';
import clsx from 'clsx';
import { Trans } from 'react-i18next';
import { Links } from '../../../application/constants/links.ts';
import Step1Image from 'assets/img/warranty-instruction-step1.svg';
import Step2Image from 'assets/img/warranty-instruction-step2.svg';
import Step3Image from 'assets/img/warranty-instruction-step3.svg';
import Step4Image from 'assets/img/warranty-instruction-step4.svg';
import Step5Image from 'assets/img/warranty-instruction-step5.svg';
import Step6Image from 'assets/img/warranty-instruction-step6.svg';
import Step7Image from 'assets/img/warranty-instruction-step7.svg';
import { Link } from 'react-router-dom';
import { Step } from 'components/uikit/Step.tsx';
import SuccessIcon from 'assets/icons/success.svg?react';
import CloseIcon from 'assets/icons/close-button.svg?react';

type CertificateStepKey =
  | 'step_1'
  | 'step_2'
  | 'step_3'
  | 'step_4'
  | 'step_5'
  | 'step_6'
  | 'step_7';

type Step = {
  textKey: CertificateStepKey;
  image: string;
  imageClassName?: string;
  link?: string;
};

const steps: Step[] = [
  {
    textKey: 'step_1',
    image: Step1Image,
    link: Links.Authorized.WarrantyRequest.route,
  },
  {
    textKey: 'step_2',
    image: Step2Image,
  },
  {
    textKey: 'step_3',
    image: Step3Image,
    imageClassName: styles.width33percent,
  },
  {
    textKey: 'step_4',
    image: Step4Image,
  },
  {
    textKey: 'step_5',
    image: Step5Image,
  },
  {
    textKey: 'step_6',
    image: Step6Image,
    imageClassName: styles.imageSix,
  },
  {
    textKey: 'step_7',
    image: Step7Image,
    link: Links.Authorized.WarrantyRequestHistory.route,
  },
];

export const WarrantyInfo = () => {
  const i18n = useScopedTranslation('Page.WarrantyInfo');

  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  return (
    <div>
      <Breadcrumbs />
      <div className={clsx(styles.marginTop8)}>
        <div className={clsx(styles.title, styles.marginBottom16)}>
          {i18n.t('sending_warranty_request.title')}
        </div>
        <div className={clsx(styles.text, styles.marginBottom16)}>
          {i18n.t('sending_warranty_request.description')}
        </div>
        <div className={clsx(styles.container)}>
          {steps.map((step, index) => {
            return (
              <div key={step.textKey} className={clsx(styles.item)}>
                <div className={styles.stepTextContainer}>
                  <Step
                    className={styles.stepNumber}
                    value={index + 1}
                    isActive={true}
                    Svg={index + 1 === steps.length ? SuccessIcon : undefined}
                    imageClassName={styles.successIcon}
                  />
                  <div className={styles.text}>
                    <Trans
                      i18nKey={`Page.WarrantyInfo.sending_warranty_request.${step.textKey}`}
                      components={{
                        bold: <strong />,
                        a: (
                          <Link to={step.link ?? ''} className={styles.link} />
                        ),
                      }}
                    />
                  </div>
                </div>
                <div
                  className={clsx(styles.imageContainer)}
                  onClick={() => setSelectedImage(step.image)}
                >
                  <img
                    src={step.image}
                    className={clsx(styles.image, step.imageClassName)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div
        className={clsx(
          styles.marginBottom48,
          styles.requestProcessingContainer,
        )}
      >
        <div className={clsx(styles.title, styles.marginBottom16)}>
          {i18n.t('request_processing.title')}
        </div>
        <div className={clsx(styles.text, styles.marginBottom16)}>
          {i18n.t('request_processing.description')}
        </div>
        <div className={clsx(styles.successContainer, styles.marginBottom16)}>
          <div className={clsx(styles.rowContainer, styles.columnGap8)}>
            <StatusSuccess />
            <div className={styles.boldText}>
              {i18n.t('request_processing.success_title')}
            </div>
          </div>
          <div
            className={clsx(
              styles.text,
              styles.marginLeft28,
              styles.marginTop4,
              styles.marginBottom2,
            )}
          >
            - {i18n.t('request_processing.success_step_1')}
          </div>
          <div className={clsx(styles.text, styles.marginLeft28)}>
            - {i18n.t('request_processing.success_step_2')}
          </div>
        </div>
        <div className={clsx(styles.warningContainer, styles.marginBottom16)}>
          <div className={clsx(styles.rowContainer, styles.columnGap8)}>
            <StatusWarning />
            <div className={styles.boldText}>
              {i18n.t('request_processing.warning_title')}
            </div>
          </div>
          <div
            className={clsx(
              styles.text,
              styles.marginLeft28,
              styles.marginTop4,
            )}
          >
            - {i18n.t('request_processing.warning_step_1')}
          </div>
        </div>
        <div className={clsx(styles.errorContainer, styles.marginBottom16)}>
          <div className={clsx(styles.rowContainer, styles.columnGap8)}>
            <StatusFailed />
            <div className={styles.boldText}>
              {i18n.t('request_processing.error_title')}
            </div>
          </div>
          <div
            className={clsx(
              styles.text,
              styles.marginLeft28,
              styles.marginTop4,
            )}
          >
            - {i18n.t('request_processing.error_step_1')}
          </div>
        </div>
      </div>
      <div className={styles.marginBottom48}>
        <div className={clsx(styles.title, styles.marginBottom16)}>
          {i18n.t('defective_parts.title')}
        </div>
        <div
          className={clsx(
            styles.warningContainer,
            styles.marginBottom16,
            styles.columnGap8,
          )}
        >
          <div
            className={clsx(
              styles.rowContainer,
              styles.text,
              styles.columnGap8,
            )}
          >
            <WarningIcon className={styles.orangeIcon} />
            <Trans
              i18nKey={'Page.WarrantyInfo.defective_parts.step_1'}
              components={{
                bold: <label className={styles.bold} />,
              }}
            />
          </div>
        </div>
        <div className={clsx(styles.rowContainer, styles.columnGap8)}>
          <div className={clsx(styles.stepContainer)}>
            <Search />
          </div>
          <div className={styles.text}>{i18n.t('defective_parts.step_2')}</div>
        </div>
      </div>
      <div>
        <div className={clsx(styles.title, styles.marginBottom16)}>
          {i18n.t('contacts.title')}
        </div>
        <div className={clsx(styles.text, styles.marginBottom16)}>
          {i18n.t('contacts.description')}
        </div>
        <div
          className={clsx(
            styles.infoContainer,
            styles.rowContainer,
            styles.text,
            styles.columnGap8,
            styles.fitContent,
          )}
        >
          <Mail className={styles.mailIcon} />
          <a
            className={styles.actionButton}
            href={'mailto:' + i18n.t('contacts.email')}
          >
            {i18n.t('contacts.email')}
          </a>
        </div>
      </div>
      {!!selectedImage && (
        <div className={styles.selectedImageWrapper}>
          <div className={styles.selectedImageContainer}>
            <img
              className={clsx(styles.image, styles.objectFitContain)}
              src={selectedImage}
            />
            <CloseIcon
              className={styles.crossIcon}
              onClick={() => setSelectedImage(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};
